import { Component } from 'js/lib/components/component'

class AmountSelector extends Component {
  initialize() {
    this.frequencyInputs = this.element.querySelectorAll('input[name*="frequency"]')
    this.amountGroups = this.element.querySelectorAll('.js-frequency-amount-group')
    this.form = this.element.closest('form')
    this.submitButton = this.form?.querySelector('.js-donation-submit')
    // Find the text span inside the button - direct child span that contains the button text
    this.submitButtonText = this.submitButton?.querySelector(':scope > span')
    
    // Cover fees component
    this.coverFeesComponent = this.form?.querySelector('.js-c-cover-fees')
    this.coverFeesPercentage = parseFloat(this.coverFeesComponent?.dataset?.percentage) || 2
    // The checkbox input is inside the form/input/checkbox component
    this.coverFeesCheckbox = this.coverFeesComponent?.querySelector('.js-c-form-input-checkbox__input')

    // Track user-chosen amounts per frequency
    this.savedSelections = {}

    this.frequencyInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateAmountGroup()
        this.updateSubmitButton()
      })
    })

    // Listen for amount changes
    this.element.addEventListener('change', (e) => {
      if (e.target.name?.includes('amount') || e.target.classList.contains('js-c-custom-amount-radio')) {
        this.saveCurrentSelection()
        this.updateSubmitButton()
      }
    })

    // Also listen for custom amount input
    this.element.addEventListener('input', (e) => {
      if (e.target.classList.contains('js-c-custom-amount-input')) {
        this.updateSubmitButton()
      }
    })

    // Listen for cover fees checkbox changes (click on the button toggles the state)
    const coverFeesButton = this.coverFeesComponent?.querySelector('.js-c-form-input-checkbox__button')
    if (coverFeesButton) {
      coverFeesButton.addEventListener('click', () => {
        // Defer to next tick so the checkbox state has updated
        setTimeout(() => this.updateSubmitButton(), 0)
      })
    }

    // Set initial state
    this.updateAmountGroup()
    this.updateSubmitButton()
  }

  updateAmountGroup() {
    const checkedInput = Array.from(this.frequencyInputs).find(input => input.checked)
    const selectedFrequency = checkedInput?.value || this.element.dataset.currentFrequency

    // Save selection from previously active group before switching
    this.saveCurrentSelection()

    this.amountGroups.forEach(group => {
      const groupFrequency = group.dataset.frequency
      const isActive = groupFrequency === selectedFrequency
      const radioInputs = group.querySelectorAll('input[name*="amount"][type="radio"]')
      const hiddenInputs = group.querySelectorAll('input[name*="amount"][type="hidden"]')
      const customTextInput = group.querySelector('.js-c-custom-amount-input')

      group.classList.toggle('tw-hidden', !isActive)
      group.classList.toggle('tw-block', isActive)

      if (isActive) {
        // Enable preset radio inputs in the active group
        radioInputs.forEach(input => { input.disabled = false })
        if (customTextInput) customTextInput.disabled = false
        this.restoreSelection(group, groupFrequency)
      } else {
        // Disable submission inputs in hidden groups to prevent form conflicts
        radioInputs.forEach(input => {
          input.disabled = true
          input.checked = false
        })
        hiddenInputs.forEach(input => { input.disabled = true })
        if (customTextInput) customTextInput.disabled = true
        // Also uncheck custom radio (has no name attribute so not in above selectors)
        const customRadio = group.querySelector('.js-c-custom-amount-radio')
        if (customRadio) customRadio.checked = false
      }
    })
  }

  saveCurrentSelection() {
    this.amountGroups.forEach(group => {
      if (group.classList.contains('tw-hidden')) return
      const frequency = group.dataset.frequency

      // Check if custom amount is selected
      const customRadio = group.querySelector('.js-c-custom-amount-radio:checked')
      if (customRadio) {
        this.savedSelections[frequency] = 'custom'
        return
      }

      const checkedRadio = group.querySelector('input[name*="amount"][type="radio"]:checked')
      if (checkedRadio) {
        this.savedSelections[frequency] = checkedRadio.value
      }
    })
  }

  restoreSelection(group, frequency) {
    const amountInputs = group.querySelectorAll('input[name*="amount"][type="radio"]')
    const savedValue = this.savedSelections[frequency]

    if (savedValue === 'custom') {
      // Restore custom amount selection
      const customRadio = group.querySelector('.js-c-custom-amount-radio')
      const customHiddenInput = group.querySelector('.js-c-custom-amount-value')
      if (customRadio) {
        // Uncheck all preset radios
        amountInputs.forEach(input => { input.checked = false })
        customRadio.checked = true
        if (customHiddenInput) customHiddenInput.disabled = false
      }
      return
    }

    if (savedValue) {
      // Restore user's previous selection
      const target = Array.from(amountInputs).find(input => input.value === savedValue)
      if (target) {
        target.checked = true
        return
      }
    }

    // Fall back to the preselected default
    const preselected = group.querySelector('input[data-preselected]')
    if (preselected) preselected.checked = true
  }

  updateSubmitButton() {
    if (!this.submitButtonText) return

    const frequency = this.getSelectedFrequency()
    const baseAmount = this.getSelectedAmount()
    const coverFees = this.isCoverFeesChecked()
    const paymentLabel = this.element.dataset.paymentLabel || 'donation'
    const frequencyLabel = frequency === 'monthly' ? 'monthly ' : (paymentLabel === 'donation' ? 'one-time ' : '')

    if (baseAmount > 0) {
      this.submitButton.disabled = false
      const coverFeesEl = this.element.closest('form')?.querySelector('.js-c-cover-fees')
      const feeRate = parseFloat(coverFeesEl?.dataset?.feeRate) || 0.026
      const feeFlat = parseFloat(coverFeesEl?.dataset?.feeFlat) || 0.37
      const fee = coverFees ? (baseAmount * feeRate + feeFlat) : 0
      const totalAmount = (baseAmount + fee).toFixed(2)
      this.submitButtonText.textContent = `Continue with your $${totalAmount} ${frequencyLabel}${paymentLabel}`
    } else {
      this.submitButton.disabled = true
      this.submitButtonText.textContent = `Continue with your ${frequencyLabel}${paymentLabel}`
    }
  }

  getSelectedFrequency() {
    const checkedInput = Array.from(this.frequencyInputs).find(input => input.checked)
    return checkedInput?.value || this.element.dataset.currentFrequency || 'once_off'
  }

  getSelectedAmount() {
    // First check if custom amount option is selected (radio checked)
    const customAmountRadio = this.element.querySelector('.js-c-custom-amount-radio:checked')
    if (customAmountRadio) {
      // Get the value from the hidden input (which mirrors the text input)
      const hiddenInput = this.element.querySelector('.js-c-custom-amount-value:not(:disabled)')
      if (hiddenInput) {
        const value = parseFloat(hiddenInput.value)
        if (!isNaN(value) && value > 0) {
          return value
        }
      }
      // Fallback: get from text input directly
      const customInput = this.element.querySelector('.js-c-custom-amount-input')
      if (customInput) {
        const cleanValue = customInput.value.replace(/[$,]/g, '')
        const value = parseFloat(cleanValue)
        if (!isNaN(value) && value > 0) {
          return value
        }
      }
      return 0
    }

    // Otherwise check preset amount radios
    const amountRadios = this.form?.querySelectorAll('input[name*="amount"][type="radio"]:checked:not(:disabled)')
    for (const radio of amountRadios || []) {
      const value = parseFloat(radio.value)
      if (!isNaN(value) && value > 0) {
        return value
      }
    }
    return 0
  }

  isCoverFeesChecked() {
    return this.coverFeesCheckbox?.checked ?? false
  }
}

AmountSelector.register('.js-c-amount-selector')

export { AmountSelector }
